// src/components/forms/FormField.jsx
import React, { useState } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';

const FormField = ({
  label,
  type = 'text',
  name,
  value,
  onChange,
  placeholder,
  error,
  required = false,
  disabled = false,
  className = '',
  autoComplete,
  maxLength,
  icon, // Added icon prop
  actionButton, // Added action button prop
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const id = `field-${name}`;
  const inputType = type === 'password' && showPassword ? 'text' : type;

  const togglePasswordVisibility = () => setShowPassword(prev => !prev);

  return (
    <div className={['form-field', className].filter(Boolean).join(' ')}>
      {label && (
        <label
          htmlFor={id}
          className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white"
        >
          {icon && (
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/40 dark:text-blue-200 dark:ring-blue-900/40">
              {icon}
            </span>
          )}

          <span className="inline-flex items-center gap-1">
            {label}
            {required && <span className="text-red-500">*</span>}
          </span>
        </label>
      )}

      <div className="relative">
        <input
          id={id}
          type={inputType}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          maxLength={maxLength}
          className={[
            'w-full',
            'rounded-xl',
            'border',
            'bg-white',
            'px-4',
            'py-3',
            'text-sm',
            'font-medium',
            'text-slate-900',
            'shadow-sm',
            'outline-none',
            'transition',
            'placeholder:text-slate-400',
            'focus:ring-2',
            'disabled:cursor-not-allowed',
            'disabled:opacity-60',
            'dark:bg-slate-900',
            'dark:text-white',
            'dark:placeholder:text-slate-500',
            error
              ? 'border-red-300 ring-red-500/20 dark:border-red-900/50 dark:ring-red-500/20'
              : 'border-slate-200 focus:border-blue-300 focus:ring-blue-500/20 dark:border-slate-800 dark:focus:border-blue-900/50 dark:focus:ring-blue-500/20',
            type === 'password' || actionButton ? 'pr-12' : '',
          ].join(' ')}
          {...props}
        />

        {type === 'password' && (
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-600 transition hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:text-slate-300 dark:hover:bg-slate-800/60 dark:hover:text-white"
            onClick={togglePasswordVisibility}
            tabIndex="-1"
            title={showPassword ? 'Hide password' : 'Show password'}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <FiEyeOff /> : <FiEye />}
          </button>
        )}

        {actionButton && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            {actionButton}
          </div>
        )}
      </div>

      {maxLength && value && (
        <div className="mt-1 text-right text-xs font-medium text-slate-500 dark:text-slate-400">
          {value.length}/{maxLength}
        </div>
      )}

      {error && (
        <div className="mt-2 text-sm font-medium text-red-600">
          {error}
        </div>
      )}
    </div>
  );
};

export default FormField;
